import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject, Subscription, takeUntil, tap } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import {
  SetCurrentElementId,
  toggle,
} from 'src/app/shared/store/shared.actions';
import { selectUnreadCount } from 'src/app/features/notifications/store/notification.selectors';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { SessionService } from 'src/app/core/services/session.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit {
  currentId!: number;
  private subscription!: Subscription;
  toggleSidebar: boolean = false;
  toggleProduits!: boolean;
  toggleCustomers!: boolean;
  toggleSettings!: boolean;
  togglePurchase!: boolean;
  toggleSalesforce!: boolean;
  toggleMarketing!: boolean;
  unreadCount = 0;
  loyaltyEnabled = false;

  /**
   * Viewport width below which the sidebar renders icon-only. Must stay in sync
   * with $sidebar-compact-breakpoint in src/styles/ui/_sidebar.scss.
   */
  private static readonly COMPACT_BREAKPOINT = 1150;

  /** True when the media query has collapsed the sidebar, independent of the toggle. */
  isCompactViewport = window.innerWidth <= SidebarComponent.COMPACT_BREAKPOINT;

  /**
   * Icon-only, whether by the manual toggle or the viewport. Nav labels are hidden
   * in this state, so the tooltips take over as the only way to read a section name.
   */
  get isCollapsed(): boolean {
    return this.toggleSidebar || this.isCompactViewport;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.isCompactViewport = window.innerWidth <= SidebarComponent.COMPACT_BREAKPOINT;
  }

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private router: Router,
    private actions$: Actions,
    private store: Store<AppState>,
    private featureFlagService: FeatureFlagService,
    private sessionService: SessionService
  ) {}

  ngOnInit(): void {
    this.actions$
      .pipe(
        ofType(SetCurrentElementId),
        tap((action) => {
          this.currentId = action.currentElementId!;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(toggle),
        tap((action: any) => {
          this.toggleSidebar = action.toggle;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.store
      .select(selectUnreadCount)
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => (this.unreadCount = count));

    this.featureFlagService.loadFeatures()
      .pipe(takeUntil(this.destroy$))
      .subscribe();

    this.featureFlagService.loyaltyEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => (this.loyaltyEnabled = enabled));
  }

  logout() {
    // Revokes the session server-side as well as locally — see SessionService.
    this.sessionService.logout();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggle(type: string) {
    switch (type) {
      case 'sidebar':
        this.toggleSidebar = !this.toggleSidebar;
        this.store.dispatch(toggle({ toggle: this.toggleSidebar }));
        break;
      case 'produits':
        this.toggleProduits = !this.toggleProduits;
        break;
      case 'customers':
        this.toggleCustomers = !this.toggleCustomers;
        break;
      case 'purchase':
        this.togglePurchase = !this.togglePurchase;
        break;
      case 'settings':
        this.toggleSettings = !this.toggleSettings;
        break;
      case 'salesforce':
        this.toggleSalesforce = !this.toggleSalesforce;
        break;
      case 'marketing':
        this.toggleMarketing = !this.toggleMarketing;
        break;
    }
  }

  hideComponent(): boolean {
    const routesToHideComponent = [
      '/quotations/add',
      '/settings',
    ];

    return !routesToHideComponent.some((route) =>
      this.router.url.startsWith(route)
    );
  }
}
